import React, { useState } from 'react'
import { Alert, Box, Button, Checkbox, CheckboxGroup, FormControl, Heading, Input, Radio, RadioGroup, SimpleModal, Stack, Text } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'
import useReauthentication from '@/hooks/useReauthentication'

// Creating an API key, in a dialog of its own.
//
// A key is fixed at creation — there is no edit path for one, by design — so
// everything that decides what it may do has to be settled here: its name, how
// long it lasts, the access it carries and the trials it may reach.
//
// `onCreated` is given the new key, whose `token` is the one moment it is
// readable; the page shows it and it is never recoverable again.

export const SCOPE_READ = 'trialflare.read'
export const SCOPE_WRITE = 'trialflare.write'

// What the API stores against a key, as something to show against it later.
// A key made before scopes existed has none and carries full access.
export function isReadOnly (scopes) {
  return Boolean(scopes) && !scopes.includes(SCOPE_WRITE)
}

export function describeScopes (scopes) {
  if (!scopes) return 'Full access'
  return isReadOnly(scopes) ? 'Read only' : 'Read and write'
}

export default function NewApiKey ({ onCreated }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [validity, setValidity] = useState('')
  const [access, setAccess] = useState('write')
  const [reach, setReach] = useState('all')
  const [chosenTrials, setChosenTrials] = useState([])
  const [busy, setBusy] = useState(false)
  const { withReauth } = useReauthentication()
  // The trials this account can reach, already loaded for the nav. The API
  // checks the same thing when the key is created.
  const { trials } = useStore()

  const reset = () => {
    setName('')
    setValidity('')
    setAccess('write')
    setReach('all')
    setChosenTrials([])
  }

  const close = () => {
    setOpen(false)
    reset()
  }

  // A key carries the whole of your account's access, so the API asks you to
  // confirm it's you before minting one.
  const create = () => {
    setBusy(true)
    const scopes = access === 'write' ? [SCOPE_READ, SCOPE_WRITE] : [SCOPE_READ]
    withReauth('Creating an API key', (proof, resolve, reject) =>
      api.accounts.createApiToken(
        {
          serviceName: name.trim(),
          validity: parseInt(validity) || 0,
          scopes,
          trials: reach === 'some' ? chosenTrials : undefined
        },
        proof,
        resolve,
        reject
      )
    ).then(token => {
      setBusy(false)
      close()
      onCreated(token)
    }).catch(err => {
      setBusy(false)
      if (!err?.cancelled) utils.toast('error', 'Unable to create your token', err.message)
    })
  }

  return (
    <SimpleModal
      isOpen={open}
      onClose={close}
      size='lg'
      header='Create an API key'
      trigger={
        <Button colorPalette='primary' size='sm' onClick={() => setOpen(true)}>
          Create key
        </Button>
      }
      body={
        <Stack gap={5}>
          <FormControl
            label='Name'
            isRequired
            helperText='An identifier (e.g. "Data warehouse sync", "REDCap bridge", etc.).'
          >
            <Input
              placeholder='Key name'
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </FormControl>

          <FormControl
            label='Expires after'
            helperText='Days from now. Leave blank for a key that never expires.'
          >
            <Input
              w='200px'
              placeholder='365'
              value={validity}
              onChange={e => setValidity(e.target.value.replace(/\D/, ''))}
            />
          </FormControl>

          <FormControl label='Access' isRequired>
            <RadioGroup
              value={access}
              colorPalette='primary'
              onValueChange={e => setAccess(e.value)}
            >
              <Stack gap={2}>
                <Radio value='write'>Read and write</Radio>
                <Radio value='read'>Read only</Radio>
              </Stack>
            </RadioGroup>
            <Text fontSize='sm' color='fg.muted' mt={2}>
              {access === 'write'
                ? 'This key can read and change anything your account can.'
                : 'This key can read and export, but cannot create, change or delete anything.'}
            </Text>
          </FormControl>

          {/* Not a FormControl: a group of checkboxes inside one all share the
              field's control id, so every label drives the first box. Use a
              heading and a CheckboxGroup, as the message wizard does. */}
          <Box>
            <Heading as='h3' size='sm' fontWeight='semibold'>Trials</Heading>
            <RadioGroup
              value={reach}
              colorPalette='primary'
              onValueChange={e => setReach(e.value)}
              mt={2}
            >
              <Stack gap={2}>
                <Radio value='all'>Every trial I can access</Radio>
                <Radio value='some' disabled={!trials?.length}>Only the trials I choose</Radio>
              </Stack>
            </RadioGroup>

            {reach === 'some' &&
              <CheckboxGroup value={chosenTrials} onValueChange={setChosenTrials} name='trials'>
                <Stack gap={2} mt={3} maxH='200px' overflowY='auto' borderWidth='1px' rounded='md' p={3}>
                  {trials.map(trial => (
                    <Checkbox key={trial._id} value={trial._id}>{trial.name}</Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>}

            <Text fontSize='sm' color='fg.muted' mt={2}>
              {reach === 'some'
                ? 'A restricted key cannot create trials or manage your team. Those are held at team level, not against a trial.'
                : 'This key reaches every trial you can access, including ones you join later.'}
            </Text>
          </Box>

          <Alert status='info' variant='left-accent'>
            A key can never do more than your own permissions allow.
          </Alert>
        </Stack>
      }
      footer={
        <>
          <Button variant='ghost' onClick={close}>Cancel</Button>
          <Button
            colorPalette='primary'
            loading={busy}
            disabled={name.trim().length < 3 || (reach === 'some' && !chosenTrials.length)}
            onClick={create}
          >
            Create key
          </Button>
        </>
      }
    />
  )
}
